const INSIGHTS_ROUTE = '/insights'
const NEW_RELIC_RETRY_DELAY_MS = 250
export const classifyInsightsNavigationCache = (performanceApi) => {
  try {
    const [navigationEntry] = performanceApi?.getEntriesByType?.('navigation') || []
    if (!navigationEntry) return 'unknown'

    if (
      navigationEntry.deliveryType === 'cache' ||
      navigationEntry.deliveryType === 'navigational-prefetch'
    ) {
      return 'cached'
    }

    const transferSize = Number(navigationEntry.transferSize)
    const encodedBodySize = Number(navigationEntry.encodedBodySize)
    if (transferSize === 0 && encodedBodySize > 0) return 'cached'
    if (transferSize === 300 && encodedBodySize > 0) return 'cached'

    // Resource Timing adds 300 bytes to a network response. A validated cache
    // response reports 300 bytes instead, so ambiguous values stay unknown.
    if (encodedBodySize > 0 && transferSize === encodedBodySize + 300) {
      return 'uncached'
    }
  } catch {
    // Cache classification is optional and must never affect the measured flow.
  }

  return 'unknown'
}

export function createInsightsPerformanceTelemetry({
  allowedNames,
  allowedOutcomes,
  browserWindow,
  performanceApi,
}) {
  const pendingEvents = []
  const recordedNames = new Set()
  let flushScheduled = false
  let navigationComplete = false
  let retryAttempted = false
  let retryTimerId = null

  const flush = () => {
    flushScheduled = false
    if (!pendingEvents.length) return

    const addPageAction = browserWindow.newrelic?.addPageAction
    if (typeof addPageAction !== 'function') {
      if (!retryAttempted) {
        retryAttempted = true
        flushScheduled = true
        retryTimerId = browserWindow.setTimeout(() => {
          retryTimerId = null
          flush()
        }, NEW_RELIC_RETRY_DELAY_MS)
        return
      }

      pendingEvents.splice(0)
      return
    }

    retryAttempted = false
    const events = pendingEvents.splice(0)
    const cacheClassification = classifyInsightsNavigationCache(performanceApi)
    events.forEach(({ name, attributes }) => {
      try {
        addPageAction.call(browserWindow.newrelic, name, {
          ...attributes,
          cacheClassification,
        })
      } catch {
        // Monitoring failures must never affect rendering or data loading.
      }
    })
  }

  const scheduleFlush = () => {
    if (!navigationComplete || flushScheduled || !pendingEvents.length) return

    flushScheduled = true
    if (typeof browserWindow.requestIdleCallback === 'function') {
      browserWindow.requestIdleCallback(flush, { timeout: 2000 })
    } else {
      browserWindow.setTimeout(flush, 0)
    }
  }

  return {
    markNavigationComplete() {
      navigationComplete = true
      scheduleFlush()
    },
    recordFirstCompletion({ name, outcome, start, end }) {
      if (
        !allowedNames.has(name) ||
        !allowedOutcomes.has(outcome) ||
        recordedNames.has(name)
      ) {
        return
      }
      recordedNames.add(name)

      if (!pendingEvents.length && retryTimerId === null) retryAttempted = false
      pendingEvents.push({
        name,
        attributes: {
          durationMs: Math.max(0, end - start),
          outcome,
          route: INSIGHTS_ROUTE,
        },
      })
      scheduleFlush()
    },
  }
}
